/* Presentation decisions for profiling history and the run-to-run comparison.
 *
 * Three rules carry over from the profile panel, and each one has a sharper edge here because two
 * measurements sit side by side.
 *
 * **A null is "not measured", never a zero.** A column that one run could not measure is
 * `not_comparable`, not a fall to or from 0%. Rendering its missing side as `0%` would turn "we did not
 * measure" into the most alarming movement the screen can show.
 *
 * **Only measures with a better direction are called better or worse.** Completeness and the baseline
 * are better higher, one value dominating a column is better lower. A distinct count that moved is
 * `changed` — calling it an improvement would invent an intent nobody declared — and the tones keep
 * that visible: `changed` is the neutral information blue, never green.
 *
 * **"Not comparable" must not look like "compared, and nothing moved".** Both are grey; the first has
 * the hollow dot the profile panel already uses for "cannot say", and a hatched segment in the result
 * bar, so the difference survives greyscale and colour-blindness.
 *
 * Everything here is pure: no clock, no request, no translation. The screen turns what these return
 * into words, so the rules can be tested without rendering anything.
 */

import type {
  ColumnComparison,
  MetricComparison,
  ProfileComparisonSummary,
  ProfileRecommendation,
  ProfileRunSummary,
} from '@/shared/api/generated/catalog';

import { count, share } from './presentation';

export type ComparisonStatus = MetricComparison['status'];
export type ComparedMetric = MetricComparison['metric'];
export type RecommendationSeverity = ProfileRecommendation['severity'];

/** The tones `.lq-cn__state` draws. `null` is the chip's own grey. */
export type ChipTone = 'ok' | 'error' | 'warn' | 'info' | null;

/** A total map, so a sixth status from a newer server fails the build here rather than rendering in
 * whichever tone a `default:` happened to give it — the defect recorded at the anomaly and evals
 * screens, where an unknown verdict took the permitted tone. */
export const STATUS_TONE = {
  improved: 'ok',
  degraded: 'error',
  changed: 'info',
  unchanged: null,
  not_comparable: null,
} as const satisfies Record<ComparisonStatus, ChipTone>;

export const SEVERITY_TONE = {
  high: 'error',
  medium: 'warn',
  low: 'info',
} as const satisfies Record<RecommendationSeverity, ChipTone>;

/** A shape beside the colour, so the three severities survive greyscale. */
export const SEVERITY_GLYPH = {
  high: '▲',
  medium: '◆',
  low: '▼',
} as const satisfies Record<RecommendationSeverity, string>;

/** The hollow "cannot say" dot. Only `not_comparable` has it. */
export function isHollow(status: ComparisonStatus): boolean {
  return status === 'not_comparable';
}

/** The order the result bar, the count tiles and the column list are read in: what needs attention
 * first, then what moved without a better direction, then what got better, and the two greys last. */
export const STATUS_ORDER = [
  'degraded',
  'changed',
  'improved',
  'not_comparable',
  'unchanged',
] as const satisfies readonly ComparisonStatus[];

const STATUS_RANK: Readonly<Record<ComparisonStatus, number>> = {
  degraded: 0,
  changed: 1,
  improved: 2,
  not_comparable: 3,
  unchanged: 4,
};

/** The three column measures, in the order the table shows them. */
export const COLUMN_METRICS = [
  'completeness',
  'distinct_count',
  'dominant_share',
] as const satisfies readonly ComparedMetric[];

/** The two dataset measures, baseline first: it is the figure a reader already knows from the page
 * header, so it anchors the pair. */
export const DATASET_METRICS = [
  'baseline',
  'rows',
] as const satisfies readonly ComparedMetric[];

function isShare(metric: string | null): boolean {
  return metric === 'completeness' || metric === 'dominant_share';
}

/** The minus sign, not a hyphen: a hyphen reads as a dash in a column of figures. */
const MINUS = '−';

function sign(value: number): string {
  return value > 0 ? '+' : MINUS;
}

// --------------------------------------------------------------------------------------- Instants

const DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const INSTANT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** A run's day, as the strip labels it: `15 Oct`. Day-first, as the rest of the product reads. */
export function dayLabel(iso: string): string {
  const instant = new Date(iso);
  return Number.isNaN(instant.getTime()) ? iso : DAY.format(instant);
}

/** A run's instant in full: `15 Oct 2026, 09:02`, in the reader's own zone. */
export function instantLabel(iso: string): string {
  const instant = new Date(iso);
  return Number.isNaN(instant.getTime()) ? iso : INSTANT.format(instant);
}

const DAY_MS = 86_400_000;

/** Whole days between two runs, for "7 days apart". */
export function daysApart(earlier: ProfileRunSummary, later: ProfileRunSummary): number {
  const gap = Date.parse(later.finished_at) - Date.parse(earlier.finished_at);
  return Number.isNaN(gap) ? 0 : Math.max(0, Math.round(gap / DAY_MS));
}

// ---------------------------------------------------------------------------------------- Figures

/** A count large enough that its last digits are noise to a reader scanning a column. */
const BIG_COUNT = 1_000_000;
/* Two decimals on both sides of an arrow, always: `1.20M → 1.53M`, not `1.2M → 1.53M`, which reads
   as two figures measured to different precisions. */
const MILLIONS = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function wholeCount(value: number): string {
  const rounded = Math.round(value);
  return Math.abs(rounded) >= BIG_COUNT ? MILLIONS.format(rounded) : count(rounded);
}

/** One side of a measure as the screen shows it, or `null` when it was not measured — the caller says
 * "not measured" in words rather than a dash that could be mistaken for a zero.
 *
 * An estimated distinct count is rounded and marked `~`: it arrives fractional, and four significant
 * figures on a figure good to about 3% would claim a precision the estimator does not have. */
export function metricValue(
  metric: string | null,
  value: number | null,
  exact = true,
): string | null {
  if (value === null) return null;
  if (isShare(metric)) return share(value);
  if (metric === 'baseline') return String(Math.round(value));
  if (metric === 'rows') return count(Math.round(value));
  return `${exact ? '' : '~'}${wholeCount(value)}`;
}

/** How far a measure moved, structured so the screen can word each kind.
 *
 * Shares move in **percentage points**: "+5.0%" would read as a relative change, a different and
 * larger claim. An estimated distinct count moves as an approximate *relative* change, because the
 * absolute difference of two estimates is mostly the estimator's own error. */
export type Movement =
  | { readonly kind: 'none' }
  | { readonly kind: 'notComparable' }
  | { readonly kind: 'points'; readonly value: string }
  | { readonly kind: 'count'; readonly value: string }
  | { readonly kind: 'approxPercent'; readonly value: string }
  | { readonly kind: 'rows'; readonly value: string; readonly percent: string | null }
  | { readonly kind: 'baseline'; readonly value: string };

/** A share movement smaller than this, in points, reads as no change. */
const SHARE_MOVE_FLOOR_POINTS = 0.05;

export function movement(metric: MetricComparison): Movement {
  const { before, after } = metric;
  if (metric.status === 'not_comparable' || before === null || after === null) {
    return { kind: 'notComparable' };
  }
  if (isShare(metric.metric)) {
    const points = (after - before) * 100;
    if (Math.abs(points) < SHARE_MOVE_FLOOR_POINTS) return { kind: 'none' };
    return { kind: 'points', value: `${sign(points)}${Math.abs(points).toFixed(1)}` };
  }
  if (metric.metric === 'baseline') {
    const delta = Math.round(after) - Math.round(before);
    if (delta === 0) return { kind: 'none' };
    return { kind: 'baseline', value: `${sign(delta)}${String(Math.abs(delta))}` };
  }
  const delta = Math.round(after) - Math.round(before);
  if (delta === 0) return { kind: 'none' };
  const signed = `${sign(delta)}${count(Math.abs(delta))}`;
  if (metric.metric === 'rows') {
    const percent =
      before > 0
        ? `${sign(delta)}${(Math.abs(after / before - 1) * 100).toFixed(1)}%`
        : null;
    return { kind: 'rows', value: signed, percent };
  }
  if (!metric.exact && before > 0) {
    return {
      kind: 'approxPercent',
      value: `${sign(delta)}${(Math.abs(delta / before) * 100).toFixed(0)}%`,
    };
  }
  return { kind: 'count', value: signed };
}

/** A share measure's bar: the later run's fill and the earlier run's tick, as percentages. `null` for
 * a count, which has no scale a bar could be drawn on, and for a side that was not measured. */
export function shareBar(
  metric: MetricComparison,
): { readonly after: number; readonly before: number } | null {
  if (!isShare(metric.metric) || metric.before === null || metric.after === null)
    return null;
  const clamp = (value: number): number => Math.min(100, Math.max(0, value * 100));
  return { after: clamp(metric.after), before: clamp(metric.before) };
}

/** A column's measure by name. The server orders them its own way; the table orders them by
 * `COLUMN_METRICS`, so a reordering on the wire cannot shuffle the columns of the table. */
export function metricOf(
  column: ColumnComparison,
  metric: ComparedMetric,
): MetricComparison | undefined {
  return column.metrics.find((each) => each.metric === metric);
}

// ---------------------------------------------------------------------------------------- Columns

/** How badly a column got worse, so two Worse columns list the larger fall first: the completeness
 * drop or the top-value rise in points, and a collapse to one value as the worst a column can do. */
export function worstMagnitude(column: ColumnComparison): number {
  let worst = 0;
  for (const metric of column.metrics) {
    if (metric.status !== 'degraded') continue;
    if (metric.metric === 'distinct_count') {
      worst = Math.max(worst, 100);
    } else if (metric.before !== null && metric.after !== null) {
      worst = Math.max(worst, Math.abs(metric.after - metric.before) * 100);
    }
  }
  return worst;
}

/** Worst first: by result, then by how far a column fell, then by name so two reads of one
 * comparison list the same order. */
export function sortColumns(
  columns: readonly ColumnComparison[],
): readonly ColumnComparison[] {
  return [...columns].sort(
    (a, b) =>
      STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
      worstMagnitude(b) - worstMagnitude(a) ||
      a.column.localeCompare(b.column),
  );
}

/** Which columns the table lists: every one, those that need a look, or one result. */
export type ColumnFilter = 'all' | 'attention' | ComparisonStatus;

/** A long comparison opens on the columns that moved, so the unchanged majority does not bury them. */
export const ATTENTION_DEFAULT_ABOVE = 20;

export function defaultFilter(columnCount: number): ColumnFilter {
  return columnCount > ATTENTION_DEFAULT_ABOVE ? 'attention' : 'all';
}

export interface VisibleColumns {
  readonly rows: readonly ColumnComparison[];
  /** Unchanged columns a "needs a look" view left out, so the screen can offer them. */
  readonly hidden: number;
}

export function visibleColumns(
  columns: readonly ColumnComparison[],
  filter: ColumnFilter,
  query: string,
): VisibleColumns {
  const wanted = query.trim().toLowerCase();
  const matching = columns.filter(
    (each) => wanted === '' || each.column.toLowerCase().includes(wanted),
  );
  const rows = matching.filter((each) =>
    filter === 'all'
      ? true
      : filter === 'attention'
        ? each.status !== 'unchanged'
        : each.status === filter,
  );
  return { rows, hidden: filter === 'attention' ? matching.length - rows.length : 0 };
}

/** Whether any count in the table is an estimate, so the legend explains the `~`. */
export function hasEstimate(columns: readonly ColumnComparison[]): boolean {
  return columns.some((column) =>
    column.metrics.some(
      (metric) =>
        metric.metric === 'distinct_count' && !metric.exact && metric.before !== null,
    ),
  );
}

// ---------------------------------------------------------------------------------------- Verdict

/** What the headline says: how many columns got worse, and which dataset measures fell. */
export interface Headline {
  readonly degraded: number;
  readonly fell: readonly ComparedMetric[];
}

export function headline(
  summary: ProfileComparisonSummary,
  datasetMetrics: readonly MetricComparison[],
): Headline {
  return {
    degraded: summary.degraded,
    fell: DATASET_METRICS.filter((name) =>
      datasetMetrics.some((each) => each.metric === name && each.status === 'degraded'),
    ),
  };
}

/** The result bar's segments, in reading order, as shares of the compared columns. Empty when no
 * column was measured in both runs. */
export function resultSegments(summary: ProfileComparisonSummary): readonly {
  readonly status: ComparisonStatus;
  readonly count: number;
  readonly share: number;
}[] {
  const total = STATUS_ORDER.reduce((sum, status) => sum + summary[status], 0);
  if (total === 0) return [];
  return STATUS_ORDER.filter((status) => summary[status] > 0).map((status) => ({
    status,
    count: summary[status],
    share: (summary[status] / total) * 100,
  }));
}

// -------------------------------------------------------------------------------- Recommendations

/** The measure a recommendation's figure is about, or `null` for a dropped column, which has none. */
export function recommendationMetric(
  recommendation: ProfileRecommendation,
): ComparedMetric | null {
  const metric = recommendation.metric;
  return metric === 'rows' ||
    metric === 'baseline' ||
    metric === 'completeness' ||
    metric === 'dominant_share' ||
    metric === 'distinct_count'
    ? metric
    : null;
}

/** The ICU arguments a recommendation's sentence takes, each formatted for its measure. A side that
 * was not measured is an empty string, which none of the sentences that can carry one reaches. */
export function recommendationValues(
  recommendation: ProfileRecommendation,
): Readonly<Record<string, string>> {
  const metric = recommendationMetric(recommendation);
  return {
    column: recommendation.column ?? '',
    before: metricValue(metric, recommendation.before) ?? '',
    after: metricValue(metric, recommendation.after) ?? '',
  };
}

export const RECOMMENDATIONS_SHOWN = 6;

// ------------------------------------------------------------------------------------- The choice

/** The later side: the newest run, or a run by its place in the list. */
export type LaterChoice = 'latest' | number;
/** The earlier side: the run just before the later one, or a run by its place in the list. */
export type EarlierChoice = 'auto' | number;

/** Why a chosen pair has nothing to show. The screen says which, rather than asking the server for a
 * comparison it would refuse. */
export type PairBlock = 'same' | 'noEarlier';

export interface Pair {
  /** Index of the later run, newest first. */
  readonly later: number;
  /** Index of the earlier run; may be past the end of the list. */
  readonly earlier: number;
  readonly block: PairBlock | null;
}

export function resolvePair(
  runCount: number,
  later: LaterChoice,
  earlier: EarlierChoice,
): Pair {
  const li = later === 'latest' ? 0 : Math.max(0, Math.min(later, runCount - 1));
  const ei = earlier === 'auto' ? li + 1 : earlier;
  const block: PairBlock | null =
    ei === li ? 'same' : ei >= runCount ? 'noEarlier' : null;
  return { later: li, earlier: ei, block };
}

/** The earlier side after the later one moved: an explicit earlier run that would now be *newer* than
 * the later one goes back to "the run before it", rather than silently comparing backwards. */
export function earlierAfterLaterMoved(
  earlier: EarlierChoice,
  later: number,
): EarlierChoice {
  return earlier !== 'auto' && earlier < later ? 'auto' : earlier;
}

// -------------------------------------------------------------------------------------- The strip

/** The strip's plot height, its scale lines and the line the unmeasured runs sit on, in pixels. */
export const STRIP = {
  top: 6,
  bottom: 56,
  unmeasured: 62,
  plot: 76,
  nodeWidth: 64,
} as const;

export interface StripNode {
  readonly run: ProfileRunSummary;
  /** Index in the newest-first list, which is what the choice is expressed in. */
  readonly index: number;
  /** Horizontal position, as a percentage of the strip's width. */
  readonly x: number;
  /** Vertical position in pixels from the strip's top, or `null` when the run had no baseline. */
  readonly y: number | null;
}

export interface StripGeometry {
  readonly nodes: readonly StripNode[];
  /** Polylines through consecutive measured runs, in the SVG's 100 × 76 viewBox. A run with no
   * baseline breaks the line rather than being drawn at zero. */
  readonly lines: readonly { readonly points: string; readonly area: string }[];
  readonly low: number;
  readonly high: number;
  /** A strip narrower than this scrolls rather than crowding its labels. */
  readonly minWidth: number;
}

/** Oldest at the left, as a reader expects time to run. The baseline scale is padded so neither the
 * best nor the worst run sits on the frame, and clamped to the 0–100 the baseline lives in. */
export function stripGeometry(runs: readonly ProfileRunSummary[]): StripGeometry {
  const total = runs.length;
  const baselines = runs.flatMap((run) =>
    run.baseline_value === null ? [] : [run.baseline_value],
  );
  const low = baselines.length > 0 ? Math.max(0, Math.min(...baselines) - 6) : 0;
  const high = baselines.length > 0 ? Math.min(100, Math.max(...baselines) + 4) : 100;
  const span = high - low || 1;
  const x = (k: number): number => (total === 1 ? 50 : 4 + (k / (total - 1)) * 92);
  const y = (value: number | null): number | null =>
    value === null
      ? null
      : STRIP.top + ((high - value) / span) * (STRIP.bottom - STRIP.top);

  const nodes = runs
    .map((run, index) => ({ run, index }))
    .reverse()
    .map(({ run, index }, k) => ({ run, index, x: x(k), y: y(run.baseline_value) }));

  const segments: [number, number][][] = [];
  let current: [number, number][] = [];
  for (const node of nodes) {
    if (node.y === null) {
      if (current.length > 0) segments.push(current);
      current = [];
    } else {
      current.push([node.x, node.y]);
    }
  }
  if (current.length > 0) segments.push(current);

  const point = ([px, py]: [number, number]): string =>
    `${px.toFixed(2)},${py.toFixed(2)}`;
  const lines = segments
    .filter((segment) => segment.length > 1)
    .map((segment) => {
      const points = segment.map(point).join(' ');
      const first = segment[0]?.[0] ?? 0;
      const last = segment[segment.length - 1]?.[0] ?? 0;
      return {
        points,
        area: `${first.toFixed(2)},${String(STRIP.unmeasured)} ${points} ${last.toFixed(2)},${String(STRIP.unmeasured)}`,
      };
    });

  return {
    nodes,
    lines,
    low,
    high,
    minWidth: Math.max(total * STRIP.nodeWidth, 280),
  };
}
